/**
 * useStableDeepValue
 * ------------------
 * `useWatch({ control })` with no `name` (a full-form watch, e.g. to drive a
 * live preview) deep-clones the entire form on every keystroke, so every
 * nested array/object gets a brand-new reference even when its own content
 * didn't change — e.g. typing in `title` still produces a new `steps`
 * reference. Downstream `useEffect`/`useMemo` dependency arrays that key off
 * those branches then re-run on every keystroke, even though that branch
 * didn't actually change.
 *
 * This hook re-clones the value each render but reuses the previous
 * render's reference for any branch that is deep-equal to before, so only
 * the branches that actually changed get a new identity.
 */
import { useRef } from "react";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === "[object Object]";

const structuralShare = (prev: unknown, next: unknown): unknown => {
    if (prev === next) return prev;

    if (Array.isArray(next)) {
        if (!Array.isArray(prev) || prev.length !== next.length) return next;
        let identical = true;
        const merged = next.map((item, index) => {
            const shared = structuralShare(prev[index], item);
            if (shared !== prev[index]) identical = false;
            return shared;
        });
        return identical ? prev : merged;
    }

    if (isPlainObject(next)) {
        if (!isPlainObject(prev)) return next;
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) return next;
        let identical = true;
        const merged: Record<string, unknown> = {};
        for (const key of nextKeys) {
            if (!(key in prev)) {
                identical = false;
                merged[key] = next[key];
                continue;
            }
            const shared = structuralShare(prev[key], next[key]);
            if (shared !== prev[key]) identical = false;
            merged[key] = shared;
        }
        return identical ? prev : merged;
    }

    return next;
};

export const useStableDeepValue = <T>(value: T): T => {
    const ref = useRef<T>(value);
    ref.current = structuralShare(ref.current, value) as T;
    return ref.current;
};
